/* tslint:disable:max-classes-per-file */
import {WorkoutsService} from './Network/WorkoutsService';
import {lazy} from './Support/lazy';
import {ApolloClient, InMemoryCache} from '@apollo/client';
import {LeaderboardService} from './Network/LeaderboardService';
import {StrokesService} from './Network/StrokesService';
import {ExportService} from './Network/ExportService';
import {MeService} from './Network/MeService';
import {TracksDownloadService} from './Network/TracksDownloadService';
import {CrewsService} from './Network/CrewsService';
import {EventsService} from './Network/EventsService';
import {EventsInteractor} from './Calendar/EventsInteractor';
import {RsvpInteractor} from './Calendar/RsvpInteractor';
import {SnippetService} from './Network/SnippetService';
import {ReportService} from './Network/ReportService';
import {CacheStore} from './Network/Cache/Cache';
import {LocalPreferences} from './Persistence/LocalPreferences';

interface NetworkComponent {
    me: MeService;
    events: EventsService;
    crews: CrewsService;
    workouts: WorkoutsService;
    strokes: StrokesService;
    tracksDownload: TracksDownloadService;
    leaderboard: LeaderboardService;
    export: ExportService;
    snippets: SnippetService;
    report: ReportService;
}

export interface Component {
    network: NetworkComponent;
    events: EventsInteractor;
    rsvp: RsvpInteractor;
    localPreferences: LocalPreferences;
}

class DefaultNetworkComponent implements NetworkComponent {
    @lazy()
    public get me(): MeService {
        return new MeService(this.apolloClient);
    }

    @lazy()
    public get events(): EventsService {
        return new EventsService(this.apolloClient);
    }

    @lazy()
    public get crews(): CrewsService {
        return new CrewsService(this.apolloClient);
    }

    @lazy()
    public get workouts(): WorkoutsService {
        return new WorkoutsService(this.apolloClient, this.publicApolloClient);
    }

    @lazy()
    public get strokes(): StrokesService {
        return new StrokesService(this.apolloClient, CacheStore.create('strokes'));
    }

    @lazy()
    public get tracksDownload(): TracksDownloadService {
        return new TracksDownloadService();
    }

    @lazy()
    public get leaderboard(): LeaderboardService {
        return new LeaderboardService(this.apolloClient);
    }

    @lazy()
    public get export(): ExportService {
        return new ExportService(this.apolloClient);
    }

    @lazy()
    public get snippets(): SnippetService {
        return new SnippetService(this.apolloClient);
    }

    @lazy()
    public get report(): ReportService {
        return new ReportService(this.apolloClient);
    }

    @lazy()
    private get apolloClient(): ApolloClient<any> {
        return new ApolloClient({
            uri: '/graphql/web',
            headers: {
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? '',
            },
            cache: new InMemoryCache(),
        });
    }

    @lazy()
    private get publicApolloClient(): ApolloClient<any> {
        return new ApolloClient({
            uri: '/graphql/public',
            headers: {
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? '',
            },
            cache: new InMemoryCache(),
        });
    }
}

class DefaultComponent implements Component {
    @lazy()
    public get network(): NetworkComponent {
        return new DefaultNetworkComponent();
    }

    @lazy()
    public get events(): EventsInteractor {
        return new EventsInteractor(this.network.events);
    }

    @lazy()
    public get rsvp(): RsvpInteractor {
        return new RsvpInteractor(this.events);
    }

    @lazy()
    public get localPreferences(): LocalPreferences {
        return new LocalPreferences();
    }
}

let instance: Component;

export function component(): Component {
    if (instance === undefined) {
        instance = new DefaultComponent();
    }

    return instance;
}
